import React, { useState } from 'react';
import { useEffectOnce } from 'react-use';
import styled from 'styled-components';
import TabMenu from '../../../CommonComponents/TabMenu';
import { urlWithParams } from '../../../CommonUtils/urlWithParams';
import { RiskProfileSummary } from '../../../RiskProfileSummary/RiskProfileSummary';
import useApi from '../../../_hooks/useApi';
import { RiskProfileDetailTable } from './RiskProfileDetailTable';
import { Alert } from '../../../CommonComponents/Alert/Alert';
import LoadingMask from '../../../CommonComponents/LoadingMask';

const Title = styled.div`
  font-weight: 600;
  font-size: 22px;
  color: #444444;
`;

const EmptyState = styled.div`
  background: #ffffff;
  border: 1px solid #e3e1e1;
  box-sizing: border-box;
  box-shadow: 0 2px 4px rgba(184, 184, 184, 0.08);
  text-align: center;
  padding: 100px 0;
  color: #6c727f;
`;

enum RiskProfileTabs {
  PORTFOLIO,
  MODEL,
}

interface Props {
  clientId: string;
}

export const RiskProfile: React.FC<Props> = ({ clientId }) => {
  const [currentTab, setCurrentTab] = useState<RiskProfileTabs>(RiskProfileTabs.PORTFOLIO);

  const [fetchRiskProfile, { loading, data, errors }] = useApi(
    urlWithParams('/Consumer/OrionAccountsWidgets/RiskAlyseRiskProfile', { clientId }),
  );

  useEffectOnce(() => {
    fetchRiskProfile();
  });

  if (errors.length > 0) {
    return <Alert message={errors} />;
  }

  if (loading) {
    return (
      <div>
        <LoadingMask isDark width="100px" height="32px" style={{ marginBottom: 48 }} />
        <LoadingMask isDark height="240px" style={{ marginBottom: 48 }} />
        <LoadingMask isDark width="100px" height="32px" style={{ marginBottom: 48 }} />
        <LoadingMask isDark height="600px" />
      </div>
    );
  }

  if (!data) {
    return (
      <>
        <Title style={{ marginBottom: 56 }}>Risk Profile</Title>
        <EmptyState>There is no information available.</EmptyState>
      </>
    );
  }

  const { riskNumber, currentPortfolio, recommendedPortfolio } = data;

  const menuItems = [
    {
      name: 'Your Portfolio',
      isActive: currentTab === RiskProfileTabs.PORTFOLIO,
      action() {
        setCurrentTab(RiskProfileTabs.PORTFOLIO);
      },
    },
  ];

  if (recommendedPortfolio) {
    menuItems.push({
      name: 'Model',
      isActive: currentTab === RiskProfileTabs.MODEL,
      action() {
        setCurrentTab(RiskProfileTabs.MODEL);
      },
    });
  }

  return (
    <div>
      <Title style={{ marginBottom: 56 }}>Risk Profile</Title>

      <RiskProfileSummary
        style={{ marginBottom: 48 }}
        riskNumber={riskNumber}
        currentPortfolio={currentPortfolio}
        recommendedPortfolio={recommendedPortfolio}
      />

      <TabMenu underlined style={{ marginBottom: 48 }} menuItems={menuItems} />

      {currentTab === RiskProfileTabs.PORTFOLIO && (
        <RiskProfileDetailTable accounts={currentPortfolio.accounts} />
      )}

      {currentTab === RiskProfileTabs.MODEL && (
        <RiskProfileDetailTable accounts={recommendedPortfolio?.accounts} />
      )}
    </div>
  );
};
